import type { FC, ReactNode } from 'react'
import styled from 'styled-components'

import Avatar from '@/components/Avatar'
import type { ReferenceTier } from '@/lib/apollo/query/get-applicant'
import type { Milestone } from '@/types/Activity'
import type { Address } from '@/utils/address'
import { getAddressFirstLine, getAddressSecondLine } from '@/utils/address'
import { getQueueDayZeroBeginning, toAgeOfReferenceLabel } from '@/utils/ageOfReference'
import { formatDate } from '@/utils/date'
import { toReferenceTierLabel } from '@/utils/tier'

const Container = styled.div`
  padding-left: 15px;
  padding-right: 15px;
  padding-top: 13px;
  padding-bottom: 13px;

  display: grid;
  grid-template-columns: min-content 1fr 1.5fr;
  grid-column-gap: 10px;
  align-items: center;

  &:hover {
    cursor: pointer;
    background: #f1f1f1;
  }
`

const NameContainer = styled.div`
  font-weight: 600;
  font-size: 16px;
`

const DetailsContainer = styled.div`
  display: grid;
  grid-row-gap: 10px;
`

const DetailContainer = styled.div`
  display: grid;
  grid-template-columns: 25px 1fr;
`

const TypeContainer = styled.span`
  text-transform: capitalize;
`

const Icon = styled.img`
  width: 12px;
  min-width: 12px;
  max-width: 12px;
  min-height: 12px;
  max-height: 12px;
  vertical-align: middle;
  margin-top: 4px;
`

const Capitalize = styled.div`
  text-transform: capitalize;
`

const getTypeLabel = (type: string): string => (type === 'applicant' ? 'tenant' : type)

interface Props {
  avatarUrl?: string
  name: string
  email: string
  type: string
  address?: Address
  milestones?: Milestone[]
  tenancyStartDate?: Date
  visitorPanel?: ReactNode
  customerClientName?: string
  referenceTier: ReferenceTier
}

const Subject: FC<Props> = ({
  avatarUrl,
  name,
  email,
  type,
  address,
  milestones,
  tenancyStartDate,
  visitorPanel,
  customerClientName,
  referenceTier,
}) => {
  return (
    <Container>
      <Avatar url={avatarUrl} name={name} />
      <DetailsContainer>
        <NameContainer>{name}</NameContainer>
        {address && (
          <DetailContainer>
            <Icon src="/assets/svg/residential.svg" alt="icon of a house" />
            <Capitalize>
              <>
                <div>{getAddressFirstLine(address)}</div>
                <div>{getAddressSecondLine(address)}</div>
              </>
            </Capitalize>
          </DetailContainer>
        )}
        <DetailContainer>
          <Icon
            src="/assets/svg/envelope.svg"
            alt="icon of a small envelope with a black outline"
          />
          <div>{email}</div>
        </DetailContainer>
        <DetailContainer>
          <Icon src="/assets/svg/guarantor.svg" alt="icon of a person" />
          <div>
            <TypeContainer>{getTypeLabel(type)}</TypeContainer> |{' '}
            {toReferenceTierLabel(referenceTier)}
          </div>
        </DetailContainer>
      </DetailsContainer>
      <DetailsContainer>
        {tenancyStartDate && (
          <DetailContainer>
            <Icon src="/assets/svg/help-the-move.svg" alt="icon of a person" />
            <div>Tenancy start date: {formatDate(tenancyStartDate)}</div>
          </DetailContainer>
        )}
        {milestones && (
          <DetailContainer>
            <Icon src="/assets/svg/ending-soon.svg" alt="icon of a person" />
            <div>
              Age of reference:{' '}
              {toAgeOfReferenceLabel(getQueueDayZeroBeginning(milestones))}
            </div>
          </DetailContainer>
        )}
        {customerClientName && (
          <DetailContainer>
            <Icon src="/assets/svg/user.svg" alt="icon of a person" />
            <div>Agency: {customerClientName}</div>
          </DetailContainer>
        )}
      </DetailsContainer>
      <div></div>
      {visitorPanel}
    </Container>
  )
}

export default Subject
